/** Where the link in the reset email lands: choose a new password using the key from the URL. */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auth } from "@/api/auth";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { AuthCard } from "@/features/auth/AuthLayout";
import { applyAuthError, type AuthFailure } from "@/features/auth/formErrors";
import { fieldMessage } from "@/lib/fieldMessage";
import { resetPasswordSchema, type ResetPasswordValues } from "@/features/auth/schemas";
import { sessionChanged } from "@/features/auth/session";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { key } = useParams({ from: "/auth/reset-password/$key" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<AuthFailure>({});
  const [done, setDone] = useState(false);

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", passwordConfirm: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFailure({});
    try {
      await auth.resetPassword(key, values.password);
      // allauth signs the user in on a successful reset, so go to the app.
      await sessionChanged(queryClient);
      await navigate({ to: "/" });
    } catch (error) {
      const applied = applyAuthError(error, {
        setError: form.setError,
        fields: ["password"],
        fallback: t("auth.errors.unexpected"),
      });
      // A 401 with no errors means the password changed but no session was granted.
      if (!applied.formError && Object.keys(form.formState.errors).length === 0) setDone(true);
      else setFailure(applied);
    }
  });

  if (done) {
    return (
      <AuthCard title={t("auth.reset.doneTitle")} description={t("auth.reset.doneBody")}>
        <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
          {t("auth.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.reset.title")} description={t("auth.reset.subtitle")}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="password"
          label={t("auth.reset.newPassword")}
          error={fieldMessage(form.formState.errors, "password", t)}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.password}
            {...form.register("password")}
          />
        </Field>
        <Field
          name="passwordConfirm"
          label={t("auth.reset.confirmPassword")}
          error={fieldMessage(form.formState.errors, "passwordConfirm", t)}
        >
          <Input
            id="passwordConfirm"
            type="password"
            autoComplete="new-password"
            invalid={!!form.formState.errors.passwordConfirm}
            {...form.register("passwordConfirm")}
          />
        </Field>
        {failure.formError && <Notice tone="error">{failure.formError}</Notice>}
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("auth.reset.working") : t("auth.reset.submit")}
        </Button>
      </form>
      <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
        {t("auth.backToSignIn")}
      </Link>
    </AuthCard>
  );
}
